<template>
    <div v-if="reservation" class="pt-1">
        <b-button-toolbar v-if="reservationEdit" class="pt-4">
            <responsive-button
                class="mr-2"
                :icon="['fas', 'upload']"
                variant="outline-primary"
                @click="showUploadDocumentModal"
                :responsiveness="false"
                :withSpace="false"
                >Upload Document</responsive-button
            >
            <responsive-button
                v-if="metadata.has_digital_signatures_enabled"
                class="signature-request-button mr-2"
                :icon="['fas', 'file-signature']"
                variant="outline-primary"
                @click="showRequestSignatureModal"
                :responsiveness="false"
                :withSpace="false"
                >Manual Signature Request</responsive-button
            >
            <responsive-button
                v-if="metadata.subscriber.guest_verification_enabled"
                class="d-none d-lg-block mr-2"
                :icon="['fas', 'user-check']"
                variant="outline-primary"
                @click="showRequestVerificationModal"
                :responsiveness="false"
                :withSpace="false"
                >Manual Verification Request</responsive-button
            >
            <b-button-group>
                <more-filters-dropdown
                    :class="`${!metadata.subscriber.guest_verification_enabled ? ' d-block d-md-none' : ''}`"
                    variant="outline-primary"
                >
                        <responsive-button
                            v-if="metadata.has_digital_signatures_enabled"
                            class="signature-request-button-dropdown"
                            :icon="['fas', 'file-signature']"
                            variant="outline-primary"
                            @click="showRequestSignatureModal"
                            :responsiveness="false"
                            :withSpace="false"
                            >Manual Signature Request</responsive-button
                        >
                        <responsive-button
                            v-if="metadata.subscriber.guest_verification_enabled"
                            class="d-lg-none d-block text-left"
                            :icon="['fas', 'user-check']"
                            variant="outline-primary"
                            @click="showRequestVerificationModal"
                            :responsiveness="false"
                            :withSpace="false"
                            >Manual Verification Request</responsive-button
                        >
                        <responsive-button
                            v-if="metadata.subscriber.guest_verification_enabled"
                            :icon="['fas', 'users']"
                            variant="outline-primary"
                            class="text-left"
                            @click="showRequestVerificationAllGuestsModal"
                            :responsiveness="false"
                            :withSpace="false"
                            >Manual Verification Request For All Guests</responsive-button
                        >
                </more-filters-dropdown>
            </b-button-group>
        </b-button-toolbar>
        <digital-signature-request-modal
            :modalId="requestSignatureModalId"
            :initialValues="reservation"
            :onUpdate="onDigitalSignatureRequest"
        />
        <guest-verification-request-modal
            :modalId="requestVerificationModalId"
            :initialValues="reservation"
            @submit="onDigitalSignatureRequest"
        />
        <guest-verification-request-modal
            :modalId="requestVerificationAllGuestsModalId"
            :initialValues="reservation"
            :allGuests="true"
            @submit="onDigitalSignatureRequest"
        />
        <validation-observer ref="upload_document_form" v-slot="{ meta, setFieldValue, errors, validate }">
            <modal
                :id="uploadDocumentModalId"
                title="Upload Document"
                ok-title="Upload"
                cancel-title="Close"
                @ok.prevent="uploadDocument"
                :ok-disabled="!meta.valid"
                @shown="validate()"
            >
                <busy-modal :show="loading"></busy-modal>
                <b-form @submit.prevent="uploadDocument">
                    <b-row>
                        <b-col>
                            <validation-provider name="description" rules="required">
                                <form-input v-model="description" floatingLabel="Description" :error="errors.description" @input="(val) => setFieldValue('description', val)" />
                            </validation-provider>
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col>
                            <validation-provider name="documentToUpload" rules="required">
                                <form-file
                                    class="overflow-hidden"
                                    :accept="acceptedFileFormats"
                                    v-model="documentToUpload"
                                    placeholder="Select a Document to Upload"
                                    :error="errors.documentToUpload"
                                    @input="(val) => setFieldValue('documentToUpload', val)"
                                ></form-file>
                            </validation-provider>
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col>
                            <form-checkbox v-model="rentalAgreement"> Rental Agreement </form-checkbox>
                        </b-col>
                    </b-row>
                </b-form>
            </modal>
        </validation-observer>
        <grid
            ref="grid"
            class="guest-invoices-grid pt-2"
            overlayNoRowsTemplate="No documents have been added"
            :columnDefs="columnDefs"
            :rowData="rowData"
            :headerHeight="45"
            :paginationPageSize="6"
            :pagination="true"
            :isAutoWidth="true"
            :isAutoHeight="true"
            :isSearch="false"
            :with-space="false"
        >
        </grid>
    </div>
</template>
<script>
import { mapGetters } from 'vuex';
import API from '../../../../../api.jsx';
import Grid from '../../../../generic/grid/grid.vue';
import Modal from '../../../../generic/modal/modal.vue';
import BusyModal from '../../../../generic/busy-modal/busy-modal.vue';
import FormFile from '../../../../generic/form-file/form-file.vue';
import FormInput from '../../../../generic/form-input/form-input.vue';
import FormSelect from '../../../../generic/form-select/form-select.vue';
import FormCheckbox from '../../../../generic/form-checkbox/form-checkbox.vue';
import ResponsiveButton from '../../../../generic/responsive-button/responsive-button.vue';
import MoreFiltersDropdown from '../../../../generic/more-filters-dropdown/more-filters-dropdown.vue';
import DigitalSignatureRequestModal from '../../../../common/digital-signature-request-modal.vue';
import { GUEST_RESERVATION_UPLOAD_DOCUMENT_ENDPOINT } from '../../../../../endpoints.js';
import { formatResponseError } from '../../../../../utils/api';
import ActionCellRenderer from './action-cell-renderer.vue';
import GuestVerificationRequestModal from '../../../../common/guest-verification-request-modal.vue';
import DownloadCellRenderer from './download-cell-renderer.vue';

export default {
    name: 'GuestInvoicesDocumentsTab',
    components: {
        Grid,
        Modal,
        BusyModal,
        FormFile,
        FormInput,
        FormSelect,
        FormCheckbox,
        ResponsiveButton,
        MoreFiltersDropdown,
        DigitalSignatureRequestModal,
        GuestVerificationRequestModal,
        actionCellRenderer: ActionCellRenderer,
        downloadCellRenderer: DownloadCellRenderer,
    },
    data() {
        return {
            loading: false,
            description: '',
            documentToUpload: null,
            rentalAgreement: false,
            uploadDocumentModalId: 'upload-document-modal-id',
            requestSignatureModalId: 'request-signature-modal-id',
            requestVerificationModalId: 'request-verification-modal-id',
            requestVerificationAllGuestsModalId: 'request-verification-modal-all-guests-id',
            acceptedFileFormats: '.png,.jpg,.jpeg,.pdf,.gif,.doc,.docx,.bmp,.csv,.xls,.xlsx,.zip,.txt',
        };
    },
    inject: ['showSuccessToast', 'showErrorToast'],
    computed: {
        ...mapGetters('guest', ['metadata']),
        ...mapGetters('guest', ['reservation']),
        guestId() {
            return parseInt(this.$route.params.id);
        },
        reservationEdit() {
            return this.metadata?.permissions?.reservation_edit;
        },
        rowData() {
            const documents = this.reservation.documents;
            const verifications = this.reservation.verifications.map((item) => ({
                ...item,
                name: item.transaction_id,
                rental_agreement: false,
                type: 'VERIFICATION',
                download_link: `/app/guest-verification/verifications/${item.id}/request-verification-image/`,
            }));
            return [...documents, ...verifications];
        },
        columnDefs() {
            return [
                {
                    field: 'created',
                    headerName: 'Created',
                    minWidth: 200,
                    maxWidth: 220,
                    type: 'dateTimeColumn',
                },
                {
                    field: 'description',
                    headerName: 'Description',
                    minWidth: 250,
                    autoWidthExclude: true,
                },
                {
                    field: 'name',
                    headerName: 'Filename',
                    minWidth: 120,
                },
                {
                    field: '',
                    headerName: '',
                    cellRenderer: 'downloadCellRenderer',
                    minWidth: 120,
                    cellClass: 'd-flex justify-content-center',
                },
                {
                    field: '',
                    headerName: '',
                    editable: false,
                    sortable: false,
                    minWidth: 63,
                    maxWidth: 63,
                    cellRendererParams: {
                        refreshReservation: this.refreshReservation,
                    },
                    cellStyle: { paddingRight: 0, paddingLeft: '1rem' },
                    cellRenderer: 'actionCellRenderer',
                    hide: !this.reservationEdit,
                },
            ];
        },
        isMobile() {
            const md = 768;
            return window.innerWidth < md || window.innerHeight < md;
        },
    },
    methods: {
        onDigitalSignatureRequest() {
            this.$emit('refreshReservations');
        },
        refreshReservation(callback) {
            this.$emit('refreshReservations', callback);
        },
        showUploadDocumentModal() {
            this.$bvModal.show(this.uploadDocumentModalId);
        },
        showRequestSignatureModal() {
            this.$bvModal.show(this.requestSignatureModalId);
        },
        showRequestVerificationModal() {
            this.$bvModal.show(this.requestVerificationModalId);
        },
        showRequestVerificationAllGuestsModal() {
            this.$bvModal.show(this.requestVerificationAllGuestsModalId);
        },
        readAsDataURL(file) {
            return new Promise((resolve, reject) => {
                const fr = new FileReader();
                fr.onerror = reject;
                fr.onload = function () {
                    resolve(fr.result);
                };
                fr.readAsDataURL(file);
            });
        },
        async uploadDocument() {
            this.loading = true;
            const data = await this.readAsDataURL(this.documentToUpload);
            const formData = {
                name: this.documentToUpload.name,
                extraData: this.documentToUpload.extraData || null,
                lastModified: this.documentToUpload.lastModified,
                lastModifiedDate: this.documentToUpload?.lastModifiedDate,
                size: this.documentToUpload.size,
                type: this.documentToUpload.type,
                data,
            };
            try {
                await API.post(GUEST_RESERVATION_UPLOAD_DOCUMENT_ENDPOINT(this.reservation.id, this.guestId), {
                    file_model: formData,
                    description: this.description,
                    rental_agreement: this.rentalAgreement,
                });
                this.$bvModal.hide(this.uploadDocumentModalId);
                this.refreshReservation(() => this.showSuccessToast({ text: 'Document successfully uploaded.' }));
            } catch (err) {
                const { generic } = formatResponseError(err);
                this.showErrorToast({
                    text: generic || `Could not upload document.`,
                });
            } finally {
                this.description = '';
                this.documentToUpload = null;
                this.rentalAgreement = false;
                this.loading = false;
            }
        },
    },
};
</script>
<style lang="scss" scoped>
@import '../../guest-invoices-grid.scss';

button.signature-request-button {
    @media (max-width: 480px) {
        display: none !important;
    }
}

.more-filters-dropdown,
button.signature-request-button-dropdown {
    @media (min-width: 480px) {
        display: none !important;
    }
}
</style>
